import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { HelpWidget } from './shared/components/help-widget/help-widget';
import { SessionReloginService } from './shared/services/session/session-relogin.service';
import { ErrorDialogService } from './shared/services/error-dialog/error-dialog';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HelpWidget],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('rateSheet');
  private readonly msalService        = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly router             = inject(Router);
  private readonly sessionRelogin     = inject(SessionReloginService);
  private readonly errorDialog        = inject(ErrorDialogService);
  private readonly _destroying$ = new Subject<void>();
  private refreshCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  private refreshInFlight = false;
  private static readonly TOKEN_REFRESH_INTERVAL_MS = 30 * 60 * 1000;
  private static readonly TOKEN_REFRESH_CHECK_MS = 60 * 1000;

  ngOnInit(): void {
    this.msalService.handleRedirectObservable().subscribe();
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        this.startTokenRefreshLoop();
      });

    // When the browser restores a page from bfcache (back/forward navigation),
    // Angular guards do not re-run and MSAL's in-memory state reflects the
    // pre-logout session. Force a reload so MSAL re-reads from storage (which
    // was cleared on logout) and the auth guard can redirect to /login.
    window.addEventListener('pageshow', this.onPageShow);
  }

  isAuthPage(): boolean {
    const url = this.router.url;
    return url.startsWith('/login') || url.startsWith('/auth-error');
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    const currentUrl = this.router.url;
    if (this.msalService.instance.getAllAccounts().length > 0) {
      const isPostRedirectUrl = currentUrl.includes('/login') || currentUrl === '/' || currentUrl.startsWith('/?') || currentUrl.startsWith('/home');
      const hasReturnUrl = !!this.sessionRelogin.getReturnUrl();
      if (isPostRedirectUrl || hasReturnUrl) {
        // After MSAL redirect, restore saved URL if present (e.g. after session expiry re-login)
        this.sessionRelogin.reset();
        this.sessionRelogin.restoreAndNavigate();
      }
    } else {
      if (!currentUrl.includes('/auth-error') && !currentUrl.includes('/login') && !currentUrl.includes('code=') && !currentUrl.includes('error=')) {
        void this.router.navigate(['/login']);
      }
    }
  }

  private redirectBaseUrlToHome(): void {
    const hasAccount = this.msalService.instance.getAllAccounts().length > 0;
    if (!hasAccount) {
      return;
    }

    const currentUrl = this.router.url;
    if (currentUrl === '/' || currentUrl.startsWith('/?')) {
      void this.router.navigateByUrl('/home', { replaceUrl: true });
    }
  }

  private startTokenRefreshLoop(): void {
    if (typeof window === 'undefined' || this.refreshCheckIntervalId) {
      return;
    }

    this.refreshCheckIntervalId = setInterval(() => {
      void this.refreshIdTokenIfStale();
    }, App.TOKEN_REFRESH_CHECK_MS);

    // Run once at startup in case token is already stale.
    void this.refreshIdTokenIfStale();
  }

  private async refreshIdTokenIfStale(): Promise<void> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      return;
    }

    const claims = account.idTokenClaims as Record<string, unknown> | undefined;

    // Background expiry check: if the token's exp has passed and the user is
    // not already on an auth page, stop the loop and show the session-expired
    // modal immediately — no API call needed, zero network overhead.
    const expiry = typeof claims?.['exp'] === 'number' ? (claims['exp'] as number) : null;
    if (expiry !== null && !this.isAuthPage() && Date.now() / 1000 > expiry) {
      this.stopTokenRefreshLoop();
      this.errorDialog.openSessionExpired(() => this.sessionRelogin.trigger());
      return;
    }

    // Stale check: proactively refresh the token before it expires.
    if (this.refreshInFlight) {
      return;
    }

    const issuedAt = typeof claims?.['iat'] === 'number' ? claims['iat'] : null;
    if (!issuedAt) {
      return;
    }

    const tokenAgeMs = Date.now() - issuedAt * 1000;
    if (tokenAgeMs < App.TOKEN_REFRESH_INTERVAL_MS) {
      return;
    }

    this.refreshInFlight = true;
    try {
      await this.msalService.instance.acquireTokenSilent({
        scopes: ['openid'],
        account,
        forceRefresh: true
      });
    } catch {
      // Keep request path behavior unchanged; interceptor handles later retries.
    } finally {
      this.refreshInFlight = false;
    }
  }

  private stopTokenRefreshLoop(): void {
    if (this.refreshCheckIntervalId) {
      clearInterval(this.refreshCheckIntervalId);
      this.refreshCheckIntervalId = null;
    }
  }

  private readonly onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) {
      // bfcache restore — reload so MSAL re-checks credentials from storage.
      window.location.reload();
    }
  };

  ngOnDestroy(): void {
    window.removeEventListener('pageshow', this.onPageShow);
    this.stopTokenRefreshLoop();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
